/**
 * Everything the Feasibility section knows, derived in one place.
 *
 * WHY THIS IS DERIVED AND NOT FETCHED: budget review reports a `feasibility`
 * object ({required, ordered, reportStatuses, present, findings}) on every run,
 * but it is written to the AGENT RUN RESULT and the stage journal — there is no
 * read route for it (`grep feasib app/routers/` finds nothing that serves it).
 * So the rules below are transcribed VERBATIM from
 * `app/services/budget_review/review.py` and the finding text from its
 * emitters, so the section and a real run can never disagree.
 *
 * Three DIFFERENT things are called "required" in the LOS; the section shows
 * both of the ones that matter and keeps them apart:
 *   A. product-level  `feasibility_required(loan_program)` — review.py:41, a
 *      substring test on the loan program. Drives the rollup + the critical
 *      finding. This is the section's `required`.
 *   B. per-property   `SubjectProperty.feasibility_required` — pipeline.py:1138,
 *      a computed DICT of four criteria. Drives the required-documents
 *      checklist (`checklists.py` fix_and_flip_requirements). This is the
 *      per-property "Property criteria" column.
 *   C. order status   `SubjectProperty.feasibilityReportStatus` — the stored
 *      select. Drives `ordered`.
 * A and B genuinely disagree (a small-budget F&F satisfies A but no criterion
 * of B), which is exactly why the table shows B per property instead of
 * collapsing everything into one "Required: Yes".
 *
 * The one thing that IS fetched is the report itself:
 * `GET /{prefix}/report_photo_reviews/{dealId}` (ReportPhotoReview) — the only
 * route carrying the vendor, the report date, the site-visit basis and the
 * vendor's budget recommendation. Its rendering rules (exact wording for the
 * two completion figures, the recommendation banner, the desk-review line) are
 * PRESCRIBED in `budgets-draws-report-photos.md` — follow that doc, don't
 * paraphrase it.
 */
import { formatDate } from "../../../../_src_shared/utils/functions/formatDate";
import { formatDateTime } from "../../../../_src_shared/utils/functions/formatDateTime";
import { addressLabel } from "../../../../_src_shared/utils/functions/addressLabel";
import { isDocHidden } from "../../../../_src_shared/utils/functions/isDocHidden";
import { FEASIBILITY_ORDERED_STATUSES } from "../../../../constants/selectOptions/selectOptionsFeasibilityReportStatus";
import { money, pct } from "./budgetDrawsTheme";

// VERBATIM from budget_review/review.py — the products that expect a
// third-party feasibility, and the docTypes that count as one. The docType list
// was widened on 2026-07-31 because the shorter one missed "Project Feasibility
// Analysis", the canonical LOS docType, so cross-refs never fired.
export const FEASIBILITY_PRODUCTS = [
  "ground up construction",
  "guc",
  "fix and flip",
  "rtl",
];
export const FEASIBILITY_DOCTYPES = [
  "Feasibility Report",
  "Feasibility",
  "Project Feasibility",
  "Construction Inspection Report",
  "Project Feasibility Analysis",
  "General Contractor Review Form (GCR)",
];

export const isFeasibilityDocType = (docType) =>
  FEASIBILITY_DOCTYPES.includes(String(docType || "").trim());

/**
 * An extracts-index row's doc type.
 *
 * The index (`GET /files/extracts/{dealId}`, built by
 * `los_documents.extracts_for_deal`) returns rows shaped
 * `{parent_document_id, source_kind, parent, parent_provenance, extracts,
 * counts}` — there is NO top-level `docType`. It lives on the joined live
 * source document, falling back to the folder's manifest provenance. Reading
 * `row.docType` silently yields undefined for every row, which is what the
 * rest of the extracts UI avoids via `parentOf(row).docType`.
 */
export const extractRowDocType = (row) =>
  row?.parent?.docType || row?.parent_provenance?.docType || null;

/** "Received" / "Reviewed" mean the UW believes a report exists. */
const RECEIVED_STATUSES = ["Received", "Reviewed"];

// ReportPhotoReview enums — Literal values from app/models/report_photos.py:32.
export const REVIEW_BASIS_LABEL = {
  site_visit: "Site visit",
  desk_review_no_site_visit: "Desk review",
  // The report's photos are the appraisal's own — a desk review even though
  // photos are present. Never labelled "Site visit".
  desk_review_appraisal_photos: "Desk review using the appraisal's photos",
};

/**
 * "Photos are from the appraisal (N of M)" — shown under the basis line when any
 * site photo matched an appraisal image. M is the report's stored photos.
 */
export const appraisalPhotoReuseLine = (review) => {
  const n = Number(review?.photosReusedFromAppraisal) || 0;
  if (n <= 0) return null;
  const m = (review?.photos || []).filter((p) => p?.imagePath).length;
  return m >= n
    ? `Photos are from the appraisal (${n} of ${m})`
    : `Photos are from the appraisal (${n})`;
};
/**
 * Prescribed sentence for a desk review (report-photos doc, "Behaviour"):
 * neutral styling, "It is information, not an error."
 */
export const DESK_REVIEW_LINE =
  "Desk review: based on the appraisal and budget, no site visit.";
export const SITE_IMAGES_LABEL = {
  site_photos: "Site photos included",
  no_site_images_included: "No site images included",
  no_images: "No images",
};
export const BUDGET_RECOMMENDATION_LABEL = {
  increase: "Vendor recommends a higher budget",
  decrease: "Vendor recommends a lower budget",
  adequate: "Adequate as proposed",
  not_stated: "Not stated",
};
/** Banner tone per the doc: amber increase · blue decrease · neutral otherwise. */
export const BUDGET_RECOMMENDATION_TONE = {
  increase: "warn",
  decrease: "info",
  adequate: "neutral",
  not_stated: "neutral",
};

/** Street + city, the short form the drilldown header chips use. */
export const propertyLabel = (p, i) => {
  const { streetNumber, streetName, city } = p?.address || {};
  const street =
    streetNumber && streetName
      ? `${streetNumber} ${streetName}`
      : (addressLabel(p?.address) || "").split(",")[0].trim();
  return [street, city].filter(Boolean).join(", ") || `Property ${i + 1}`;
};

/**
 * The per-property criteria of `SubjectProperty.feasibility_required`, as chips
 * carrying the ACTUAL figure rather than a bare "true". Keys and thresholds are
 * verbatim from pipeline.py:1138.
 */
export const criteriaChips = (sp) => {
  const crit = sp?.feasibility_required;
  if (!crit || typeof crit !== "object") return [];
  const out = [];
  if (crit.budget) {
    out.push({
      key: "budget",
      label: `Budget ${money(sp?.constructionBudget)} (over $150k)`,
    });
  }
  if (crit.change_of_use)
    out.push({ key: "change_of_use", label: "Change of use" });
  if (crit.expansion) {
    out.push({
      key: "expansion",
      label: `Expansion ${pct(sp?.squareFootageChange, 0)} (over 20%)`,
    });
  }
  if (crit.budget_vs_purchase) {
    out.push({
      key: "budget_vs_purchase",
      label: "Budget over purchase price",
    });
  }
  return out;
};

const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/**
 * @param {object} args
 * @param {string} args.productType      loan.loanProductType
 * @param {object[]} args.properties     loan.subjectProperties
 * @param {object[]} args.loanDocuments  loan.loanDocuments
 * @param {object[]} args.extractDocs    the extracts index rows
 * @param {object[]} args.reviews        ReportPhotoReview[] (newest first)
 */
export const buildFeasibility = ({
  productType,
  properties,
  loanDocuments,
  extractDocs,
  reviews,
}) => {
  const program = String(productType || "").toLowerCase();
  const required = FEASIBILITY_PRODUCTS.some((k) => program.includes(k));

  const perProperty = (properties || []).filter(Boolean).map((sp, i) => ({
    id: String(sp?._id ?? i),
    property: sp,
    label: propertyLabel(sp, i),
    statusValue: sp?.feasibilityReportStatus || null,
    criteria: criteriaChips(sp),
  }));

  const orderedRows = perProperty.filter((r) =>
    FEASIBILITY_ORDERED_STATUSES.includes(r.statusValue),
  );
  const receivedRows = perProperty.filter((r) =>
    RECEIVED_STATUSES.includes(r.statusValue),
  );
  const reviewedRows = perProperty.filter((r) => r.statusValue === "Reviewed");
  const ordered = orderedRows.length > 0;

  // Only feasibility reviews — the same collection carries draw inspections.
  const feasibilityReviews = (reviews || []).filter(
    (r) => r?.reportKind === "feasibility",
  );

  // Backend `present` means "an extract is on file". The extracts index can be
  // gated or unavailable, so a feasibility DOCUMENT is the fallback signal and
  // every label says which one it is. A ReportPhotoReview only exists for an
  // extracted report, so its presence is proof of extraction on its own.
  const extracted =
    feasibilityReviews.length > 0 ||
    (extractDocs || []).some((d) => isFeasibilityDocType(extractRowDocType(d)));
  const docs = (loanDocuments || []).filter(
    (d) => d && !isDocHidden(d) && isFeasibilityDocType(d?.docType),
  );
  const onFile = docs.length > 0 || extracted;

  /**
   * One entry per property a report resolved to, newest report first
   * (report-photos doc: "On a multi-property loan show the report under that
   * property. `null` means unresolved: show it under 'Unassigned'").
   */
  const reviewGroups = [];
  feasibilityReviews.forEach((review) => {
    const pid = review?.subjectPropertyId
      ? String(review.subjectPropertyId)
      : null;
    if (reviewGroups.some((g) => g.propertyId === pid)) return; // newest wins
    const row = pid ? perProperty.find((r) => r.id === pid) : null;
    reviewGroups.push({
      propertyId: pid,
      label: row?.label || (pid ? "Unknown property" : "Unassigned"),
      review,
    });
  });

  // The conflict worth shouting about: someone marked the report received but
  // nothing is on the loan, so every downstream check is silently not running.
  const conflictRows = onFile ? [] : receivedRows;

  const steps = [
    {
      key: "required",
      label: "Required",
      done: required,
      sub: required
        ? `${productType || "This"} program`
        : "Not required for this product",
    },
    {
      key: "ordered",
      label: "Ordered",
      done: ordered,
      sub: ordered
        ? `${orderedRows.length} of ${plural(perProperty.length, "property", "properties")}`
        : "Not ordered",
    },
    {
      key: "onFile",
      label: "On file",
      done: onFile,
      sub: conflictRows.length
        ? "Marked received, missing"
        : docs.length
          ? plural(docs.length, "document", "documents")
          : "Nothing on file",
    },
    {
      key: "extracted",
      label: "Extracted",
      done: extracted,
      sub: feasibilityReviews[0]?.analyzedAt
        ? formatDateTime(feasibilityReviews[0].analyzedAt, "date")
        : extracted
          ? "Extract on file"
          : "—",
    },
    {
      key: "reviewed",
      label: "Reviewed",
      done: reviewedRows.length > 0,
      sub: reviewedRows.length
        ? `${reviewedRows.length} of ${plural(perProperty.length, "property", "properties")}`
        : "—",
    },
  ];
  // The first unfinished step is the one being waited on.
  const currentIdx = steps.findIndex((s) => !s.done);

  // ── Header status pill ────────────────────────────────────────────────────
  const status = !required
    ? { tone: "neutral", label: "Not required" }
    : !onFile
      ? { tone: "warn", label: ordered ? "Awaiting report" : "Not ordered" }
      : !extracted
        ? { tone: "info", label: "Awaiting extraction" }
        : reviewedRows.length
          ? { tone: "good", label: "Reviewed" }
          : { tone: "good", label: "Extracted" };

  // ── Callout ───────────────────────────────────────────────────────────────
  let callout = null;
  if (conflictRows.length) {
    const names = conflictRows.map((r) => r.label).join(", ");
    callout = {
      tone: "warn",
      lead: `${names} ${conflictRows.length === 1 ? "is" : "are"} marked ${
        conflictRows.length === 1 ? "received" : "received/reviewed"
      }, but no report is on file.`,
      rest: "Upload it or assign a document already in Documents.",
    };
  } else if (required && !onFile) {
    callout = {
      tone: "warn",
      lead: `A third-party feasibility is required for this ${productType || "construction"} loan and is not on file.`,
      rest: ordered
        ? "One is marked as ordered — follow up on receipt."
        : "None is marked as ordered on the subject properties.",
    };
  } else if (onFile && !extracted) {
    callout = {
      tone: "info",
      lead: "The report is on file but has not been extracted yet.",
      // Re-typing a document never triggers extraction — it is an explicit
      // POST /files/parse_extract_docs/{dealId}, dispatched by docType
      // (extractor_router.py:110). "Run extraction" is offered in Manage rather
      // than dead-ending here.
      rest: "The cross-checks below run once extraction completes.",
    };
  } else {
    // ALWAYS a callout, even when there is nothing wrong: it is the strip the
    // Manage button lives in, and that has to stay reachable with the section
    // collapsed. A settled loan gets the quiet version.
    callout = {
      tone: "good",
      lead: "Feasibility report on file.",
      rest: reviewedRows.length
        ? "Extracted and reviewed."
        : "Extracted — the cross-checks below ran against it.",
    };
  }

  // ── Findings ──────────────────────────────────────────────────────────────
  // ONLY the emitter that is fully decidable from data the frontend can read.
  // `candidate_findings` has no read route, so this is computed, not fetched —
  // which means it must not guess at findings whose inputs live elsewhere (the
  // budget-vs-recommended variance compares the BORROWER BUDGET document's
  // statedTotalBudget, which this section never sees; the vendor's own
  // proposed-vs-recommended delta is `budgetRecommendation`, shown as the
  // report's recommendation banner rather than invented as a second finding).
  const findings = [];
  if (required && !onFile) {
    findings.push({
      key: "feasibility_missing",
      severity: "critical",
      chip: "Critical · not waivable",
      title: "Feasibility report missing",
      description: ordered
        ? "One is marked as ordered — follow up on receipt."
        : "None is marked as ordered on the subject properties.",
    });
  }

  return {
    required,
    ordered,
    onFile,
    extracted,
    docs,
    reviewGroups,
    perProperty,
    conflictRows,
    steps,
    currentIdx,
    status,
    callout,
    findings,
    /** Nothing to say at all — the section hides itself. */
    empty: !required && !ordered && !onFile,
  };
};

/** The Report panel's key/value rows. Always rendered, "—" until extracted. */
export const reportFacts = (review) => [
  { label: "Vendor", value: review?.reportVendor || null },
  {
    label: "Report type",
    value: review?.reportKind
      ? review.reportKind === "feasibility"
        ? "Feasibility"
        : "Inspection"
      : null,
  },
  {
    label: "Review basis",
    value: review?.reviewBasis
      ? REVIEW_BASIS_LABEL[review.reviewBasis] || review.reviewBasis
      : null,
  },
  // The report's own printed date — a calendar date, so formatDate (which reads
  // the date part directly) rather than a timezone-converting timestamp render.
  { label: "Report date", value: formatDate(review?.reportDate, null) },
  {
    label: "Site images",
    value: review?.siteImagesStatus
      ? SITE_IMAGES_LABEL[review.siteImagesStatus] || review.siteImagesStatus
      : null,
  },
];
